import {
  SHButton,
  SHDotGroup,
  SHHtmlBlock,
  SHLabel,
  SHStack,
} from "@components/design-systems";
import { DownloadSVG, PaperClipSVG } from "@components/svgs";
import {
  CompositeValueDTO,
  FieldTypeDTO,
  InvestmentProductModelsDTO,
  ProductModelValueDTO,
} from "@models/product/investment-product/entities/model";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  areCompositeValuesEqual,
  FeaturePath,
  FeatureProductDataPath,
  generateDefaultFileUrl,
  getFeaturePaths,
  getFeatureProductDataPaths,
  getFeatureAndProductDataPathByStaticId,
  getModelUploadPath,
  getSectionPath,
  getSeriesPath,
  SectionPath,
} from "../../utils";
import { SHUploadFile } from "@components/design-systems/sh-upload-file";
import DocumentUploadPreview from "@pages/document-library/components/document-upload-preview";
import { getExcelRichText, hexToRGBA } from "@utils";
import {
  downloadDocument,
  autoCalculatePerformance,
} from "@services/investment/investmentService";
import {
  DOCUMENT_SIZE_LIMIT,
  DocumentUploadAcceptFileExtensions,
} from "@pages/document-library/constant";
import {
  InvestmentProductCalculationSectionStaticId,
  PerformanceFileNameTemplate,
} from "@pages/suppliers/constants";
import { isEmpty, isEqual, toString } from "lodash";
import { useSingleFileHandler } from "@hooks/useDocumentLibrary";
import { theme } from "@themes";
import { uploadOptions } from "@components/design-systems/sh-upload-file/config";
import { FieldTypeId, HighlightType } from "@models/configuration";
import { RootState, useAppDispatch } from "@redux/store";
import { updateLoadingAutoCalculate } from "@redux/slices/product/investment-product";
import { useNotification } from "@hooks/useNotification";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { InvestmentProductConfigurationFeature } from "@models/product/investment-product/entities/investmentProduct";
import { tryParseJson } from "@pages/suppliers/_id/util";
import { InvestmentDataStatus } from "@models/product/investment-product/enums/status";
import { EditProductDefaults } from "@pages/suppliers/_id/_products/investment-product/_id/edit/constants";
interface UploadDocumentProps {
  selectedIndex: [number, number];
  investmentProductId: string;
  modelId?: string;
}

export const ModelCalculatePerformanceDocumentUpload = ({
  investmentProductId,
  modelId = "",
  selectedIndex,
}: UploadDocumentProps) => {
  const { notify } = useNotification();
  const { control, setValue, getValues } = useFormContext<{
    investmentProductModels: InvestmentProductModelsDTO[];
  }>();
  const managerName = useSelector(
    (state: RootState) =>
      state.investmentProduct.investmentProductUI.investmentProductName,
  );
  const dispatch = useAppDispatch();
  const modelFieldPath = getModelUploadPath(selectedIndex);
  const seriesPath = getSeriesPath(selectedIndex[0]);
  const defaultFileUrl = generateDefaultFileUrl();
  const isDefaultFile = isEqual(
    getValues(modelFieldPath.performanceFileUrl),
    defaultFileUrl,
  );
  const isPublishedFile = isEqual(
    getValues(modelFieldPath.performanceFileUrl),
    getValues(modelFieldPath.publishedPerformanceFileUrl),
  );
  const isShowOrangeDot = useMemo(() => {
    const shouldHideDot = isPublishedFile || isDefaultFile;
    return !shouldHideDot;
  }, [isPublishedFile, isDefaultFile]);

  const autoCalculationSectionStaticIds = [
    InvestmentProductCalculationSectionStaticId.PortfolioReturns,
    InvestmentProductCalculationSectionStaticId.RiskMeasures,
    InvestmentProductCalculationSectionStaticId.OtherDetails,
  ];

  const updateCompositeDataStatus = (
    productDataPath: FeatureProductDataPath,
    featurePath: FeaturePath,
    jsonValue: string,
    feature: InvestmentProductConfigurationFeature,
  ) => {
    const originalValue = feature.productData?.value;
    const isModified = !isEqual(jsonValue, originalValue);
    const productDataValues = tryParseJson(jsonValue) as ProductModelValueDTO[];
    if (!Array.isArray(productDataValues)) return;

    const isFilled = productDataValues.every((item) => !isEmpty(item.value));
    setValue(
      productDataPath.dataStatus,
      isFilled ? InvestmentDataStatus.Filled : InvestmentDataStatus.MissingData,
    );
    setValue(
      featurePath.dataStatus,
      isFilled ? InvestmentDataStatus.Filled : InvestmentDataStatus.MissingData,
    );

    const publishedValue = toString(getValues(productDataPath.publishedValue));
    setValue(
      productDataPath.highlightType,
      !areCompositeValuesEqual(publishedValue, jsonValue)
        ? HighlightType.Edited
        : null,
    );
    setValue(
      featurePath.highlightType,
      !areCompositeValuesEqual(publishedValue, jsonValue)
        ? HighlightType.Edited
        : null,
    );
    setValue(productDataPath.isModified, isModified);
  };

  const updateDifferentCompositeDataStatus = (
    productDataPath: FeatureProductDataPath,
    feature: InvestmentProductConfigurationFeature,
    featurePath: FeaturePath,
  ) => {
    setValue(
      productDataPath.highlightType,
      !isEqual(
        getValues(productDataPath.publishedValue) as string,
        feature.value ?? "",
      )
        ? HighlightType.Edited
        : null,
    );
    setValue(
      productDataPath.dataStatus,
      isEmpty(feature.value)
        ? InvestmentDataStatus.MissingData
        : InvestmentDataStatus.Filled,
    );
    setValue(
      featurePath.dataStatus,
      isEmpty(feature.value)
        ? InvestmentDataStatus.MissingData
        : InvestmentDataStatus.Filled,
    );
  };

  const handleAutoCalculated = async (s3link: string) => {
    const excludedMetrics: string[] = [];
    const paths = getFeatureAndProductDataPathByStaticId({
      staticIds: [
        ...Object.values(InvestmentProductCalculationSectionStaticId),
      ],
      selectedIndex,
      data: getValues().investmentProductModels,
    });

    Object.keys(paths).forEach((staticId) => {
      const path = paths[staticId].featurePaths.isAdminNA;
      const value = getValues(path) as boolean;
      if (!!value) {
        excludedMetrics.push(staticId);
      }
    });

    const { data, isSuccess, message } = await autoCalculatePerformance(
      investmentProductId ?? "",
      modelId ?? "new",
      s3link,
      excludedMetrics,
    );

    if (!isSuccess || !data) {
      notify(message, {
        variant: "error",
        close: true,
      });

      return;
    }

    const formData = getValues()?.investmentProductModels;
    const [productIndex, modelIndex] = selectedIndex;
    data.sections?.forEach((section) => {
      const sStaticId = section?.staticId;
      // find section by staticId
      const sectionIndex = formData?.[productIndex]?.models?.[
        modelIndex
      ]?.sections?.findIndex((section) => section.staticId === sStaticId);
      const sectionPath = getSectionPath(selectedIndex, sectionIndex ?? -1);

      section?.features?.forEach((feature, featureIndex) => {
        const featureStaticId = feature?.staticId;
        const featurePath = getFeaturePaths(
          selectedIndex,
          sectionIndex ?? -1,
          featureIndex,
        );
        const featureProductDataPath = getFeatureProductDataPaths(
          selectedIndex,
          sectionIndex ?? -1,
          featureIndex,
        );

        switch (featureStaticId) {
          case InvestmentProductCalculationSectionStaticId.Disclaimer: {
            if (!isEmpty(feature.value)) {
              let highlightType: HighlightType | null;

              const publishedValue = getValues(
                featureProductDataPath.publishedValue,
              );
              const publishedAdditionalValue = getValues(
                featureProductDataPath.publishedAdditionalValue,
              );

              highlightType =
                !isEqual(publishedValue, "true") ||
                !isEqual(publishedAdditionalValue, feature.value)
                  ? HighlightType.Edited
                  : null;

              setValue(featureProductDataPath.highlightType, highlightType);
              setValue(
                featureProductDataPath.dataStatus,
                InvestmentDataStatus.Filled,
              );
              setValue(featureProductDataPath.value, "true");
              setValue(featureProductDataPath.additionalValue, feature.value);
              setValue(featureProductDataPath.isModified, true);
            }
            break;
          }
          case InvestmentProductCalculationSectionStaticId.NominatedPlatform: {
            if (!isEmpty(feature.value)) {
              setValue(
                featureProductDataPath.highlightType,
                !isEqual(
                  getValues(featureProductDataPath.publishedValue) ?? "",
                  feature.value ?? "",
                )
                  ? HighlightType.Edited
                  : null,
              );
              setValue(
                featureProductDataPath.dataStatus,
                isEmpty(feature.value)
                  ? InvestmentDataStatus.MissingData
                  : InvestmentDataStatus.Filled,
              );
              setValue(featureProductDataPath.value, feature.value);
              setValue(featureProductDataPath.isModified, true);
            }
            break;
          }
          default: {
            const fieldTypeId = getValues(
              featurePath.fieldType,
            ) as FieldTypeDTO;
            setValue(featureProductDataPath.isModified, true);
            if (fieldTypeId?.id === FieldTypeId.DateTime) {
              setValue(featureProductDataPath.value, feature.value);
              updateDifferentCompositeDataStatus(
                featureProductDataPath,
                feature,
                featurePath,
              );
            } else {
              const productDataParse = JSON.parse(feature.value as string);
              if (
                section.staticId ===
                InvestmentProductCalculationSectionStaticId.PortfolioReturns
              ) {
                const updatedValue = productDataParse.map(
                  (item: CompositeValueDTO) => {
                    return {
                      ...item,
                      value: item.value === "N/A" ? "" : item.value,
                    };
                  },
                );
                setValue(
                  featureProductDataPath.value,
                  JSON.stringify(updatedValue),
                );
                updateCompositeDataStatus(
                  featureProductDataPath,
                  featurePath,
                  JSON.stringify(updatedValue),
                  feature,
                );
              } else {
                setValue(featureProductDataPath.value, feature.value);
                updateCompositeDataStatus(
                  featureProductDataPath,
                  featurePath,
                  feature.value ?? "",
                  feature,
                );
              }
            }
            break;
          }
        }
        setValue(featurePath.isModified, true);
      });
      setValue(sectionPath.isModified, true);
      setValue(sectionPath.highlightType, HighlightType.Edited);
    });

    setValue(modelFieldPath.performanceFileUrl, s3link, uploadOptions.hookForm);
    setValue(modelFieldPath.isModified, true);
    setValue(modelFieldPath.isPerformanceAutoCalculated, true);
    setValue(modelFieldPath.needToPersistPerformanceData, true);
  };
  const { isUploading, uploadMessage, handleOnFileUpload } =
    useSingleFileHandler({
      onSuccess: (_, data) => {
        dispatch(updateLoadingAutoCalculate(true));
        handleAutoCalculated(data.url).then(() => {
          dispatch(updateLoadingAutoCalculate(false));
        });
      },
      onInvalidFile: () => {
        setValue(
          modelFieldPath.performanceFileUrl,
          null,
          uploadOptions.hookForm,
        );
        dispatch(updateLoadingAutoCalculate(false));
        setValue(modelFieldPath.needToPersistPerformanceData, false);
      },
      onDeleteSuccess: () => {
        setValue(
          modelFieldPath.performanceFileUrl,
          null,
          uploadOptions.hookForm,
        );
        dispatch(updateLoadingAutoCalculate(false));
        setValue(modelFieldPath.needToPersistPerformanceData, false);
      },
      onError: () => {
        dispatch(updateLoadingAutoCalculate(false));
        setValue(modelFieldPath.needToPersistPerformanceData, false);
      },
    });

  const link = useMemo(() => {
    return getValues(modelFieldPath.performanceFileUrl ?? modelFieldPath.name);
  }, [getValues, modelFieldPath]);

  const handleDownloadFile = async () => {
    const seriesName = String(getValues(seriesPath.name) ?? "");
    const modelName = String(getValues(modelFieldPath.name) ?? "");

    const paths = getFeatureAndProductDataPathByStaticId({
      staticIds: [
        InvestmentProductCalculationSectionStaticId.Disclaimer,
        InvestmentProductCalculationSectionStaticId.NominatedPlatform,
        InvestmentProductCalculationSectionStaticId.CalmarRatio,
      ],
      selectedIndex,
      data: getValues().investmentProductModels,
    });

    const hasDisclaimer =
      getValues(
        paths[InvestmentProductCalculationSectionStaticId.Disclaimer]
          .productDataPaths.value,
      ) === "true";
    const disclaimer = hasDisclaimer
      ? getValues(
          paths[InvestmentProductCalculationSectionStaticId.Disclaimer]
            .productDataPaths.additionalValue,
        )
      : "";
    const nominatedPlatform =
      getValues(
        paths[InvestmentProductCalculationSectionStaticId.NominatedPlatform]
          .productDataPaths.value,
      ) ?? "";

    const fileRequest = {
      fileUrl: getValues(
        modelFieldPath.performanceFileUrl ?? modelFieldPath.name,
      ) as string,
      disclaimer: getExcelRichText({ htmlString: String(disclaimer) }),
      managerName: managerName ?? "",
      seriesName: seriesName,
      modelName: modelName,
      nominatedPlatform: String(nominatedPlatform),
    };
    const response = await downloadDocument(
      investmentProductId,
      modelId ?? "new",
      fileRequest,
    );

    if (!response.isSuccess || !response.data) {
      notify("Failed to download document!", {
        variant: "error",
        close: true,
      });

      return;
    }

    saveAs(
      response.data,
      PerformanceFileNameTemplate({
        managerName: managerName,
        seriesName: seriesName,
        modelName: modelName,
        date: format(new Date(), "yyyyMMdd"),
      }),
    );
  };

  const handleUpdateSectionData = (
    features: InvestmentProductConfigurationFeature[],
    sectionPath: SectionPath,
  ) => {
    const isAnyFeatureModified = features?.some(
      (feature: InvestmentProductConfigurationFeature) =>
        feature.isModified === true,
    );
    const isAnyFeatureEdited = features?.some(
      (feature: InvestmentProductConfigurationFeature) =>
        feature.highlightType === HighlightType.Edited,
    );
    const isAnyFeatureMissingData = features?.some(
      (feature: InvestmentProductConfigurationFeature) =>
        feature.dataStatus === InvestmentDataStatus.MissingData,
    );

    setValue(
      sectionPath.dataStatus,
      isAnyFeatureMissingData
        ? InvestmentDataStatus.MissingData
        : InvestmentDataStatus.Filled,
    );
    setValue(sectionPath.isModified, isAnyFeatureModified);
    setValue(
      sectionPath.highlightType,
      isAnyFeatureEdited ? HighlightType.Edited : null,
    );
  };

  const resetToLastPublishedData = () => {
    const [productIndex, modelIndex] = selectedIndex;
    const selectedModels =
      getValues()?.investmentProductModels?.[productIndex]?.models?.[
        modelIndex
      ];
    const sections = selectedModels?.sections;

    setValue(
      modelFieldPath.performanceFileUrl,
      selectedModels?.publishedPerformanceFileUrl,
    );
    setValue(
      modelFieldPath.isPerformanceAutoCalculated,
      selectedModels?.isPublishedPerformanceAutoCalculated,
    );
    sections?.forEach((section, sectionIndex) => {
      const sStaticId = section?.staticId;

      // find section by staticId
      const isAutoCalculationSection = autoCalculationSectionStaticIds.includes(
        sStaticId as InvestmentProductCalculationSectionStaticId,
      );
      if (!isAutoCalculationSection) return;

      const sectionPath = getSectionPath(selectedIndex, sectionIndex ?? -1);

      section?.features?.forEach((feature, featureIndex) => {
        const featureStaticId = feature?.staticId;
        const featurePath = getFeaturePaths(
          selectedIndex,
          sectionIndex ?? -1,
          featureIndex,
        );
        const featureProductDataPath = getFeatureProductDataPaths(
          selectedIndex,
          sectionIndex ?? -1,
          featureIndex,
        );

        switch (featureStaticId) {
          case InvestmentProductCalculationSectionStaticId.Disclaimer: {
            const latestPublishedAdditionalValue =
              feature.productData?.publishedAdditionalValue;
            const latestPublishedValue = feature.productData?.publishedValue;

            setValue(featureProductDataPath.highlightType, null);
            setValue(
              featureProductDataPath.dataStatus,
              !isEmpty(latestPublishedAdditionalValue)
                ? InvestmentDataStatus.Filled
                : InvestmentDataStatus.MissingData,
            );
            setValue(featureProductDataPath.value, latestPublishedValue);
            setValue(
              featureProductDataPath.additionalValue,
              latestPublishedAdditionalValue,
            );
            setValue(featureProductDataPath.isModified, true);
            break;
          }
          case InvestmentProductCalculationSectionStaticId.NominatedPlatform: {
            const latestPublishedValue = feature.productData?.publishedValue;
            setValue(featureProductDataPath.highlightType, null);
            setValue(
              featureProductDataPath.dataStatus,
              !isEmpty(latestPublishedValue)
                ? InvestmentDataStatus.Filled
                : InvestmentDataStatus.MissingData,
            );
            setValue(featureProductDataPath.value, latestPublishedValue);
            setValue(featureProductDataPath.isModified, true);
            break;
          }
          default: {
            const fieldTypeId = getValues(
              featurePath.fieldType,
            ) as FieldTypeDTO;
            setValue(featureProductDataPath.isModified, true);
            if (fieldTypeId?.id === FieldTypeId.DateTime) {
              const latestPublishedValue = feature.productData?.publishedValue;
              setValue(featureProductDataPath.value, latestPublishedValue);
              setValue(featureProductDataPath.highlightType, null);
              setValue(
                featureProductDataPath.dataStatus,
                isEmpty(latestPublishedValue)
                  ? InvestmentDataStatus.MissingData
                  : InvestmentDataStatus.Filled,
              );
            } else {
              const latestPublishedValue = feature.productData?.publishedValue;
              setValue(featureProductDataPath.value, latestPublishedValue);
              updateCompositeDataStatus(
                featureProductDataPath,
                featurePath,
                latestPublishedValue ?? "",
                feature,
              );
            }
            break;
          }
        }
        setValue(featurePath.isModified, true);
        setValue(featurePath.highlightType, null);
      });
      setValue(sectionPath.isModified, true);
      setValue(modelFieldPath.needToPersistPerformanceData, true);
      handleUpdateSectionData(section?.features ?? [], sectionPath);
      setValue(modelFieldPath.isModified, true);
    });
  };

  const restorePublishedPerformanceData = () => {
    dispatch(updateLoadingAutoCalculate(true));
    setTimeout(() => {
      resetToLastPublishedData();
      dispatch(updateLoadingAutoCalculate(false));
    }, EditProductDefaults.DebounceTime);
  };

  return (
    <SHStack py={4}>
      <SHStack
        flexDirection="row"
        flexWrap={{ sm: "wrap", lg: "nowrap" }}
        gap={{ sm: 2, lg: 4 }}
      >
        <SHStack width={"100%"} gap={1}>
          <SHStack direction="row" spacing={0.5} alignItems="start">
            <SHLabel label={"Performance series upload"} />
            <SHDotGroup blueDot={false} orangeDot={isShowOrangeDot} />
          </SHStack>
          <SHHtmlBlock
            variant="body3"
            content="SuitabilityHub automatically calculates portfolio returns and risk measures. 
        Download the template below, enter your monthly return series data, and then upload the completed version in the box to the right."
          />
          <SHButton
            onClick={() => {
              handleDownloadFile();
            }}
            startIcon={<DownloadSVG width={20} height={20} />}
            variant="contained"
            size="extraMedium"
            sx={{
              width: "320px",
            }}
          >
            Download
          </SHButton>
        </SHStack>

        <SHStack
          sx={{
            width: "520px",
            borderRadius: "2px",
            border: "1px solid #ddd",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Controller
            rules={{
              required: "Document attachment is required",
            }}
            control={control}
            name={modelFieldPath.performanceFileUrl}
            render={({ field: { ref, ...other } }) => (
              <>
                <SHStack
                  width={"520px"}
                  direction={"row"}
                  justifyContent={"center"}
                  sx={{
                    padding: "8px 17px",
                  }}
                >
                  <SHUploadFile
                    fileSupportText="XLSX only"
                    width={other.value && !isDefaultFile ? 280 : 480}
                    height={150}
                    buttonText="Click to add"
                    isUploading={isUploading}
                    error={!isUploading && !!uploadMessage}
                    helperText={
                      !isUploading
                        ? uploadMessage
                        : "Uploading document, please wait."
                    }
                    fileIcon={<PaperClipSVG />}
                    dropzoneOptions={{
                      multiple: false,
                      accept: DocumentUploadAcceptFileExtensions,
                      onDrop: async (acceptedFiles, fileRejections) => {
                        await handleOnFileUpload(
                          acceptedFiles,
                          fileRejections,
                          DOCUMENT_SIZE_LIMIT,
                        );
                      },
                      disabled: isUploading,
                      maxSize: DOCUMENT_SIZE_LIMIT,
                    }}
                  />
                  {link && !isDefaultFile && (
                    <SHStack
                      height={150}
                      flex={1}
                      direction={"column"}
                      justifyContent={"center"}
                      alignItems={"center"}
                      sx={{
                        paddingX: 1.5,
                        paddingY: 0.5,
                        backdropFilter: "blur(0.5px)",
                        background: `${hexToRGBA("#c5ddfd", 0.1)}`,
                        border: `1px solid ${theme.palette.secondary[100]}`,
                        "& .MuiAvatar-img": {
                          paddingY: "8px !important",
                        },
                      }}
                    >
                      <DocumentUploadPreview
                        isAttaching={isUploading}
                        s3Object={{ key: link as string, url: link as string }}
                        isShowBinButton={!isDefaultFile && !isPublishedFile}
                        tooltipTitle="Undo Upload"
                        onDelete={restorePublishedPerformanceData}
                      />
                    </SHStack>
                  )}
                </SHStack>
              </>
            )}
          />
        </SHStack>
      </SHStack>
    </SHStack>
  );
};
